import React, { useState, useEffect } from 'react';
import { fetchNewVsReturning } from '../services/api';

const fmt = n => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const now = new Date();
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function NewVsReturning() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());

  const load = () => {
    setLoading(true);
    setError(null);
    fetchNewVsReturning(month, year)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const rows = Array.isArray(data) ? data : data?.data ?? data?.records?.data ?? [];
  const totalNew = rows.filter(r => r.isNew || r.type === 'New').reduce((s, r) => s + Number(r.orderCount ?? r.count ?? 1), 0);
  const totalRet = rows.filter(r => !r.isNew || r.type === 'Returning').reduce((s, r) => s + Number(r.orderCount ?? r.count ?? 1), 0);
  const totalOrders = rows.reduce((s, r) => s + Number(r.orderCount ?? r.count ?? 1), 0);
  const totalRevenue = rows.reduce((s, r) => s + Number(r.totalRevenue ?? r.totalOrderAmount ?? r.revenue ?? 0), 0);

  // Try to detect summary/aggregated shape
  const isSummary = rows.length > 0 && (rows[0].type !== undefined || rows[0].isNew !== undefined);

  const years = [];
  for (let y = now.getFullYear(); y >= 2019; y--) years.push(y);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>New vs Returning Customers</h2>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">Period</div>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>MONTH</div>
            <select value={month} onChange={e => setMonth(Number(e.target.value))} className="form-select" style={{ fontSize: 11 }}>
              {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>YEAR</div>
            <select value={year} onChange={e => setYear(Number(e.target.value))} className="form-select" style={{ fontSize: 11 }}>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <button onClick={load} className="btn btn-primary" style={{ fontSize: 11 }}>Run Report</button>
        </div>
      </div>

      {rows.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12 }}>
          {[
            { label: 'Total Orders', value: totalOrders.toLocaleString() },
            { label: 'New Customers', value: totalNew.toLocaleString(), highlight: true },
            { label: 'Returning', value: totalRet.toLocaleString() },
            { label: 'Total Revenue', value: fmt(totalRevenue) },
          ].map(({ label, value, highlight }) => (
            <div key={label} className="card" style={{ padding: '10px 14px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: highlight ? 'var(--orange)' : 'var(--navy)' }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div className="card-header">Breakdown — {MONTHS[month - 1]} {year}</div>
        {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
        {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
        {!loading && !error && rows.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No data found.</div>
        )}
        {rows.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>{isSummary ? 'Type' : 'Date'}</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Orders</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Revenue</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Avg Order</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const orders = Number(row.orderCount ?? row.count ?? 0);
                  const rev = Number(row.totalRevenue ?? row.totalOrderAmount ?? row.revenue ?? 0);
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)' }}>
                      <td style={{ padding: '8px 12px', color: 'var(--navy)', fontWeight: 600 }}>
                        {isSummary ? (row.type ?? (row.isNew ? 'New' : 'Returning') ?? '—') :
                          row.date ? new Date(row.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                      </td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--orange)', fontWeight: 700 }}>{orders.toLocaleString()}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right' }}>{fmt(rev)}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--gray-500)' }}>{orders > 0 ? fmt(rev / orders) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
